import { motion } from 'framer-motion';
import { ChartBarIcon } from '@heroicons/react/24/outline';
import type { UsageStats } from '@types';
import { getUsagePercentage, isUnlimitedTier } from '@/types/subscription';

interface UsageMeterProps {
  usageStats: UsageStats;
}

export default function UsageMeter({ usageStats }: UsageMeterProps) {
  const { current_period, history } = usageStats;

  // Calculate usage percentage from NIB employee-based stats
  const percentage = Math.min(
    getUsagePercentage(
      current_period.employees_processed,
      current_period.employee_limit
    ),
    100
  );

  // Calculate remaining employees
  const isUnlimited = isUnlimitedTier(current_period.employee_limit);
  const remaining = isUnlimited
    ? -1
    : Math.max(
        0,
        current_period.employee_limit - current_period.employees_processed
      );

  // Determine color based on usage
  const getColor = () => {
    if (percentage < 70) return 'text-green-600 bg-green-600';
    if (percentage < 90) return 'text-yellow-600 bg-yellow-600';
    return 'text-red-600 bg-red-600';
  };

  const color = getColor();
  const [textColor, bgColor] = color.split(' ');

  // Calculate circle progress
  const radius = 70;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percentage / 100) * circumference;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
      className="bg-white overflow-hidden shadow rounded-lg"
    >
      <div className="px-4 py-5 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Usage This Period
          </h3>
          <ChartBarIcon className="h-6 w-6 text-gray-400" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Circular Progress */}
          <div className="flex flex-col items-center justify-center">
            <div className="relative">
              <svg className="transform -rotate-90" width="160" height="160">
                {/* Background circle */}
                <circle
                  cx="80"
                  cy="80"
                  r={radius}
                  stroke="currentColor"
                  strokeWidth="12"
                  fill="transparent"
                  className="text-gray-200"
                />
                {/* Progress circle */}
                <motion.circle
                  cx="80"
                  cy="80"
                  r={radius}
                  stroke="currentColor"
                  strokeWidth="12"
                  fill="transparent"
                  strokeLinecap="round"
                  className={bgColor}
                  initial={{ strokeDashoffset: circumference }}
                  animate={{ strokeDashoffset: offset }}
                  transition={{ duration: 1, ease: 'easeOut' }}
                  style={{
                    strokeDasharray: circumference,
                  }}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className={`text-3xl font-bold ${textColor}`}>
                  {percentage.toFixed(0)}%
                </span>
                <span className="text-xs text-gray-500">Used</span>
              </div>
            </div>

            <div className="mt-4 text-center">
              <p className="text-sm font-medium text-gray-900">
                {current_period.employees_processed.toLocaleString()} /{' '}
                {isUnlimited
                  ? 'Unlimited'
                  : current_period.employee_limit.toLocaleString()}
              </p>
              <p className="text-xs text-gray-500">Employees Processed</p>
            </div>
          </div>

          {/* Stats */}
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">
                  Remaining Capacity
                </span>
                <span className="text-sm font-bold text-gray-900">
                  {isUnlimited ? 'Unlimited' : remaining.toLocaleString()}
                </span>
              </div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">
                  Submissions
                </span>
                <span className="text-sm font-bold text-blue-600">
                  {current_period.submissions_count.toLocaleString()}
                </span>
              </div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">
                  Employee Limit
                </span>
                <span className="text-sm font-bold text-indigo-600">
                  {isUnlimited
                    ? 'Unlimited'
                    : current_period.employee_limit.toLocaleString()}
                </span>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200">
              <p className="text-xs text-gray-500 mb-2">Billing Period</p>
              <p className="text-sm text-gray-900">
                {new Date(current_period.period_start).toLocaleDateString()} -{' '}
                {new Date(current_period.period_end).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>

        {/* Monthly History */}
        {history && history.length > 0 && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h4 className="text-sm font-medium text-gray-900 mb-3">
              Recent Activity
            </h4>
            <div className="space-y-3">
              {history.slice(0, 3).map((month, index) => {
                const maxEmployees = Math.max(
                  ...history.map(h => h.employees_processed),
                  1
                );
                const monthPercentage =
                  (month.employees_processed / maxEmployees) * 100;

                return (
                  <div key={month.month}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-gray-700">
                        {month.month}
                      </span>
                      <span className="text-sm font-medium text-gray-900">
                        {month.employees_processed} employees •{' '}
                        {month.submissions} submissions
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <motion.div
                        className="bg-indigo-600 h-2 rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: `${monthPercentage}%` }}
                        transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}
